/**
 * A deliberately small markdown subset for customer-facing T&C bodies.
 *
 * ### Why a subset, and why hand-written
 *
 * Two constraints rule out a library, and they pull in the same direction.
 *
 * The quote body is pasted into Outlook, so its HTML must carry inline styles
 * and no classes or `<style>` block — output no general-purpose renderer
 * produces. The check-in body is rendered on a public, anonymous page from
 * operator-authored content, where the safest posture is a parser that has no
 * way to emit anything but the tags listed here. A permissive parser plus a
 * sanitiser gets to the same place by removing capability; this gets there by
 * never having it. Raw HTML in the source is escaped, always, in every emitter.
 *
 * ### The subset
 *
 *   - paragraphs, with a single newline kept as a hard break
 *   - `**bold**`, `*italic*` / `_italic_`
 *   - `-` / `*` / `+` bullet lists, `1.` numbered lists
 *   - `#`, `##`, `###` headings
 *   - `---` horizontal rules
 *   - `>` blockquotes
 *   - `[text](url)`, restricted to http / https / mailto
 *   - `\` escapes the next punctuation character
 *
 * Everything else — tables, images, code fences, footnotes, raw HTML — renders
 * as the literal characters the author typed.
 *
 * ### A single newline is a hard break
 *
 * CommonMark folds a lone newline into a space; this does not. The bodies this
 * renders were written as plain text under `white-space: pre-wrap`, where a
 * newline was a newline, and an author who breaks a line means it. Blank-line
 * separated paragraphs still work as expected.
 *
 * The parse step is exposed separately from the emitters because the two front
 * ends render the AST into React elements rather than injecting HTML — no
 * `dangerouslySetInnerHTML` on a public page. The HTML emitter exists for the
 * surfaces that genuinely need an HTML string: the Outlook clipboard copy, and
 * the check-in confirmation email.
 *
 * ### This file has a Python port — change both together
 *
 * The confirmation email is composed in Python, so `termsBodyToHtml` is mirrored
 * in sm4rt-api as `core/services/terms_markdown.py`. The customer reads the
 * terms on the intake page (this file) and then receives an email embedding the
 * same frozen text (the port); if the two disagree, a legal artefact disagrees
 * with itself.
 *
 * `sm4rt-api/tests/test_terms_markdown.py` pins the port against fixtures
 * generated from THIS implementation, so a change here fails that build until
 * the port follows. After editing the subset or the styles, regenerate them:
 *
 *   cd contracts && npm run build
 *   cd ../../sm4rt-api && node scripts/generate_terms_markdown_fixtures.js
 */

import type { TermsBodyFormat } from '../contracts/termsFormat';

// ---------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'link'; href: string; children: InlineNode[] }
  /** A single newline inside a paragraph or list item. */
  | { type: 'break' };

export type BlockNode =
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'heading'; level: 1 | 2 | 3; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] }
  | { type: 'blockquote'; children: BlockNode[] }
  | { type: 'rule' };

// ---------------------------------------------------------------------------
// Block patterns
// ---------------------------------------------------------------------------

/** `---`, `***` or `___` alone on a line. Three or more, nothing else. */
const RULE_RE = /^ {0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const HEADING_RE = /^ {0,3}(#{1,3})\s+(.*)$/;
const QUOTE_RE = /^ {0,3}>\s?(.*)$/;
const BULLET_RE = /^ {0,3}([-*+])\s+(.*)$/;
const ORDERED_RE = /^ {0,3}(\d{1,9})[.)]\s+(.*)$/;

/** True when a line would open a new block, so a paragraph must stop. */
function startsBlock(line: string): boolean {
  return (
    RULE_RE.test(line) ||
    HEADING_RE.test(line) ||
    QUOTE_RE.test(line) ||
    BULLET_RE.test(line) ||
    ORDERED_RE.test(line)
  );
}

/**
 * Parse a body into blocks. Never throws: anything unrecognised degrades to
 * paragraph text, which is the same thing `'text'` format would have shown.
 */
export function parseRestrictedMarkdown(
  source: string | null | undefined,
): BlockNode[] {
  if (!source) return [];
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: BlockNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push({ type: 'rule' });
      i += 1;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length, 3) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, children: parseInline(heading[2]) });
      i += 1;
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && lines[i].trim() !== '') {
        const match = QUOTE_RE.exec(lines[i]);
        // A non-`>` line inside a quote is a lazy continuation — markdown's
        // rule, and the one an author relies on without knowing it.
        quoted.push(match ? match[1] : lines[i].trim());
        i += 1;
      }
      blocks.push({
        type: 'blockquote',
        children: parseRestrictedMarkdown(quoted.join('\n')),
      });
      continue;
    }

    const listMatch = BULLET_RE.exec(line) ?? ORDERED_RE.exec(line);
    if (listMatch) {
      const ordered = ORDERED_RE.test(line);
      const items: string[] = [];

      while (i < lines.length && lines[i].trim() !== '') {
        const current = lines[i];
        const item = ordered
          ? ORDERED_RE.exec(current)
          : BULLET_RE.exec(current);
        if (item) {
          items.push(item[2]);
        } else if (startsBlock(current) || items.length === 0) {
          // A different list marker, or any other block opener, ends this list.
          break;
        } else {
          // A plain line under an item continues it — wrapped prose in a long
          // clause, not a new bullet.
          items[items.length - 1] += ` ${current.trim()}`;
        }
        i += 1;
      }

      blocks.push({
        type: 'list',
        ordered,
        items: items.map((item) => parseInline(item)),
      });
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !startsBlock(lines[i])) {
      paragraph.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'paragraph', children: parseInlineLines(paragraph) });
  }

  return blocks;
}

// ---------------------------------------------------------------------------
// Inline parsing
// ---------------------------------------------------------------------------

/** Punctuation `\` may escape. Anything else keeps its backslash. */
const ESCAPABLE = new Set([
  '\\',
  '*',
  '_',
  '[',
  ']',
  '(',
  ')',
  '#',
  '>',
  '-',
  '+',
  '.',
  '!',
  '`',
]);

const LINK_RE = /^\[([^\]]*)\]\(([^)\s]*)\)/;

/** Parse several source lines into one run, with hard breaks between them. */
function parseInlineLines(lines: string[]): InlineNode[] {
  const nodes: InlineNode[] = [];
  lines.forEach((line, index) => {
    if (index > 0) nodes.push({ type: 'break' });
    nodes.push(...parseInline(line));
  });
  return nodes;
}

/**
 * Parse one line of inline markup.
 *
 * Scans left to right rather than running regexes over the whole string, so an
 * unclosed marker degrades to a literal character instead of swallowing the
 * rest of the paragraph.
 */
export function parseInline(source: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let text = '';
  let i = 0;

  const flush = () => {
    if (text) {
      nodes.push({ type: 'text', value: text });
      text = '';
    }
  };

  while (i < source.length) {
    const ch = source[i];

    if (ch === '\\' && i + 1 < source.length && ESCAPABLE.has(source[i + 1])) {
      text += source[i + 1];
      i += 2;
      continue;
    }

    if (ch === '[') {
      const link = LINK_RE.exec(source.slice(i));
      if (link) {
        const [matched, label, href] = link;
        flush();
        const children = parseInline(label);
        if (isSafeHref(href)) {
          nodes.push({ type: 'link', href: href.trim(), children });
        } else {
          // An unusable scheme is not an error the customer should see: keep
          // the words, drop the link. `javascript:` never reaches an emitter.
          nodes.push(...children);
        }
        i += matched.length;
        continue;
      }
    }

    if (ch === '*' && source[i + 1] === '*') {
      const close = findClosing(source, i + 2, '**');
      if (close !== -1) {
        flush();
        nodes.push({
          type: 'strong',
          children: parseInline(source.slice(i + 2, close)),
        });
        i = close + 2;
        continue;
      }
    }

    if (ch === '*' || ch === '_') {
      const close = findClosing(source, i + 1, ch);
      if (close !== -1) {
        const inner = source.slice(i + 1, close);
        // Three ways a marker is not emphasis, all of them common in terms
        // text: `2 * 3` (spaces around the content), a stray unmatched marker
        // (no closer, handled above), and `field_name_here` — an underscore
        // between word characters is part of the word, never emphasis.
        const intraword =
          ch === '_' &&
          (/[A-Za-z0-9]/.test(source[i - 1] ?? '') ||
            /[A-Za-z0-9]/.test(source[close + 1] ?? ''));
        if (!intraword && inner.trim() !== '' && !/^\s|\s$/.test(inner)) {
          flush();
          nodes.push({ type: 'em', children: parseInline(inner) });
          i = close + 1;
          continue;
        }
      }
    }

    text += ch;
    i += 1;
  }

  flush();
  return nodes;
}

/** Index of the next unescaped `marker` at or after `from`, or -1. */
function findClosing(source: string, from: number, marker: string): number {
  for (let i = from; i <= source.length - marker.length; i += 1) {
    if (source[i] === '\\') {
      i += 1;
      continue;
    }
    if (source.startsWith(marker, i)) return i;
  }
  return -1;
}

/**
 * Whether a link target may be emitted as an anchor.
 *
 * Allow-list, not a block-list: http, https and mailto. `javascript:`,
 * `data:`, `vbscript:` and every scheme invented after this was written fail
 * by default. Schemeless targets are refused too — a relative URL on the
 * check-in SWA and in an Outlook paste mean different things, and neither is
 * what the author meant.
 */
export function isSafeHref(href: string | null | undefined): boolean {
  if (!href) return false;
  const trimmed = href.trim();
  if (!trimmed) return false;
  // A control character or space embedded in the target is how a
  // `java\nscript:` href slips past a scheme test. Reject those first, so
  // the allow-list below only ever sees a clean string.
  if (/[\u0000-\u0020\u007f]/.test(trimmed)) return false;
  return /^(https?:\/\/|mailto:)/i.test(trimmed);
}

// ---------------------------------------------------------------------------
// Emitters
// ---------------------------------------------------------------------------

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Inline styles for the Outlook paste. Sizes are in `em` so the block adapts
 * to whichever container it lands in — the quote's 12px terms footer today,
 * something else tomorrow — instead of fighting it.
 */
const STYLE = {
  paragraph: 'margin:0 0 12px 0;',
  heading: [
    'font-size:1.25em;font-weight:bold;margin:16px 0 8px 0;',
    'font-size:1.12em;font-weight:bold;margin:14px 0 6px 0;',
    'font-size:1em;font-weight:bold;margin:12px 0 4px 0;',
  ],
  list: 'margin:0 0 12px 0;padding-left:22px;',
  listItem: 'margin:0 0 4px 0;',
  blockquote:
    'margin:0 0 12px 0;padding:0 0 0 12px;border-left:3px solid #e0e0e0;color:#555;',
  rule: 'border:0;border-top:1px solid #e0e0e0;margin:16px 0;',
  link: 'color:#1565c0;text-decoration:underline;',
};

function inlineToHtml(nodes: InlineNode[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case 'text':
          return escapeHtml(node.value);
        case 'break':
          return '<br />';
        case 'strong':
          return `<strong>${inlineToHtml(node.children)}</strong>`;
        case 'em':
          return `<em>${inlineToHtml(node.children)}</em>`;
        case 'link':
          return `<a href="${escapeHtml(node.href)}" style="${STYLE.link}">${inlineToHtml(
            node.children,
          )}</a>`;
        default:
          return '';
      }
    })
    .join('');
}

function blocksToHtml(blocks: BlockNode[]): string {
  return blocks
    .map((block) => {
      switch (block.type) {
        case 'paragraph':
          return `<p style="${STYLE.paragraph}">${inlineToHtml(block.children)}</p>`;
        case 'heading':
          return `<div style="${STYLE.heading[block.level - 1]}">${inlineToHtml(
            block.children,
          )}</div>`;
        case 'list': {
          const tag = block.ordered ? 'ol' : 'ul';
          const items = block.items
            .map((item) => `<li style="${STYLE.listItem}">${inlineToHtml(item)}</li>`)
            .join('');
          return `<${tag} style="${STYLE.list}">${items}</${tag}>`;
        }
        case 'blockquote':
          return `<div style="${STYLE.blockquote}">${blocksToHtml(block.children)}</div>`;
        case 'rule':
          return `<hr style="${STYLE.rule}" />`;
        default:
          return '';
      }
    })
    .join('');
}

/** Render markdown source to inline-styled, email-safe HTML. */
export function markdownToInlineStyledHtml(
  source: string | null | undefined,
): string {
  return blocksToHtml(parseRestrictedMarkdown(source));
}

function inlineToPlainText(nodes: InlineNode[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case 'text':
          return node.value;
        case 'break':
          return '\n';
        case 'strong':
        case 'em':
          return inlineToPlainText(node.children);
        case 'link': {
          const label = inlineToPlainText(node.children);
          // Keep the target: a plain-text paste that drops the URL loses the
          // only way the reader can follow it.
          return !label || label === node.href
            ? node.href
            : `${label} (${node.href})`;
        }
        default:
          return '';
      }
    })
    .join('');
}

function blocksToPlainText(blocks: BlockNode[], indent = ''): string[] {
  const out: string[] = [];
  for (const block of blocks) {
    switch (block.type) {
      case 'paragraph':
        out.push(
          indent +
            inlineToPlainText(block.children).replace(/\n/g, `\n${indent}`),
        );
        break;
      case 'heading':
        out.push(indent + inlineToPlainText(block.children));
        break;
      case 'list':
        out.push(
          block.items
            .map(
              (item, index) =>
                `${indent}${block.ordered ? `${index + 1}.` : '-'} ${inlineToPlainText(
                  item,
                ).replace(/\n/g, ' ')}`,
            )
            .join('\n'),
        );
        break;
      case 'blockquote':
        out.push(blocksToPlainText(block.children, `${indent}> `).join('\n\n'));
        break;
      case 'rule':
        out.push(`${indent}---`);
        break;
      default:
        break;
    }
  }
  return out;
}

/** Flatten markdown source to readable plain text (the clipboard variant). */
export function markdownToPlainText(source: string | null | undefined): string {
  return blocksToPlainText(parseRestrictedMarkdown(source)).join('\n\n');
}

// ---------------------------------------------------------------------------
// Format-aware helpers — what callers actually reach for
// ---------------------------------------------------------------------------

/**
 * Render a resolved T&C body to HTML, honouring the template's format.
 *
 * `'text'` keeps today's behaviour exactly: escape, then `\n` → `<br />`.
 * Callers should not branch on the format themselves — there is one right
 * answer per format and this is where it lives.
 */
export function termsBodyToHtml(
  body: string | null | undefined,
  format: TermsBodyFormat,
): string {
  if (!body) return '';
  if (format === 'markdown') return markdownToInlineStyledHtml(body);
  return escapeHtml(body).replace(/\n/g, '<br />');
}

/** The plain-text counterpart, for the clipboard's `text/plain` flavour. */
export function termsBodyToPlainText(
  body: string | null | undefined,
  format: TermsBodyFormat,
): string {
  if (!body) return '';
  return format === 'markdown' ? markdownToPlainText(body) : body;
}
